import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle2, AlertTriangle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Card } from '../ui/card';
import { ResolvedParsedPosition } from '../ImportDataModal';
import { getPositions } from '../../services/api';
import { Position } from '../../types';

interface ParsedTranche {
  orderId: string;
  symbol: string;
  isin: string;
  type: 'buy' | 'sell';
  quantity: number;
  avgPrice: number;
  timestamp: string;
  exchange: string;
  isAlreadyImported: boolean;
}

interface ExistingPositionsScreenProps {
  parsedData: ResolvedParsedPosition[];
  portfolioId: string;
  onContinue: (existingPositionsData: PositionTrancheData[]) => void;
}

// Extended tranche with selection and stoploss data
interface TrancheWithSelection extends ParsedTranche {
  selected: boolean;
  stoplossValue: string;
  maintainResidualRisk: boolean;
  calculatedStoploss: number | null;
}

export interface PositionTrancheData {
  isin: string;
  ticker: string;
  exchange: 'NSE' | 'BSE';
  bse_scrip_code?: string;
  positionId: string;
  currentTrancheCount: number;
  allTranches: TrancheWithSelection[];  // All tranches sorted chronologically
  newTranches: ParsedTranche[];         // For backward compatibility - derived from selected tranches
  duplicateTranches: ParsedTranche[];   // Already imported tranches
  stoplossData: any[];                  // For backward compatibility
  stoploss?: number;
  avgBuyPrice?: number;
  selected: boolean;
  latestBuyTrancheIndex: number;
}

const ExistingPositionsScreen: React.FC<ExistingPositionsScreenProps> = ({
  parsedData,
  portfolioId,
  onContinue,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [existingPositions, setExistingPositions] = useState<Position[]>([]);
  const [positionData, setPositionData] = useState<PositionTrancheData[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showValidationSummary, setShowValidationSummary] = useState(false);

  useEffect(() => {
    loadExistingPositions();
  }, [portfolioId, parsedData]);

  const loadExistingPositions = async () => {
    setIsLoading(true);
    
    try {
      const response = await getPositions(portfolioId);
      
      if (response.success) {
        const positions = response.data.positions;
        setExistingPositions(positions);
        
        const matchedData: PositionTrancheData[] = [];
        
        for (const parsed of parsedData) {
          const existingPos = positions.find(p => p.isin === parsed.isin);
          
          if (existingPos) {
            // Sort all tranches chronologically
            const sortedTranches = [...parsed.tranches].sort((a, b) => 
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );
            
            const currentTrancheCount = existingPos.trancheCount || 0;
            
            // Check if there are any new tranches (buy or sell)
            const hasNewTranches = sortedTranches.some(t => !t.isAlreadyImported);
            
            // Create tranches with selection and stoploss data
            let newBuyTrancheIndex = 0;
            const tranchesWithSelection: TrancheWithSelection[] = sortedTranches.map((tranche) => {
              const isNew = !tranche.isAlreadyImported;
              const isBuy = tranche.type === 'buy';
              
              // Default 10% stoploss for first new buy tranche (or when no existing tranches)
              let defaultStoploss = '';
              if (isNew && isBuy) {
                if (newBuyTrancheIndex === 0 || currentTrancheCount === 0) {
                  defaultStoploss = String(Math.round(tranche.avgPrice * 0.9 * 100) / 100);
                }
                newBuyTrancheIndex++;
              }
              
              return {
                ...tranche,
                selected: isNew,  // New tranches selected by default
                stoplossValue: defaultStoploss,
                maintainResidualRisk: false,
                calculatedStoploss: null,
              };
            });
            
            // Only add if there are tranches
            if (tranchesWithSelection.length > 0) {
              const newTranches = tranchesWithSelection.filter(t => !t.isAlreadyImported);
              const duplicateTranches = sortedTranches.filter(t => t.isAlreadyImported);
              
              matchedData.push({
                isin: parsed.isin,
                ticker: parsed.ticker,
                exchange: parsed.exchange,
                bse_scrip_code: parsed.bse_scrip_code,
                positionId: existingPos.id,
                currentTrancheCount,
                allTranches: tranchesWithSelection,
                newTranches: newTranches,
                duplicateTranches: duplicateTranches,
                stoplossData: [],  // Will be computed from allTranches
                stoploss: existingPos.stoploss,
                avgBuyPrice: existingPos.avgBuyPrice,
                selected: hasNewTranches,  // Enable if ANY new tranches (buy or sell)
                latestBuyTrancheIndex: -1,  // Will be computed dynamically
              });
            }
          }
        }
        
        setPositionData(matchedData);
      }
    } catch (error) {
      console.error('Error loading positions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle individual tranche selection
  const handleToggleTrancheSelection = (posIndex: number, trancheIndex: number, checked: boolean) => {
    setPositionData(prev => {
      const updated = [...prev];
      const tranche = updated[posIndex].allTranches[trancheIndex];
      
      // Can't select already imported tranches
      if (tranche.isAlreadyImported) return prev;
      
      tranche.selected = checked;
      
      // Update position-level selection based on whether any new tranche is selected
      const hasSelectedTranche = updated[posIndex].allTranches.some(
        t => t.selected && !t.isAlreadyImported
      );
      updated[posIndex].selected = hasSelectedTranche;
      
      return updated;
    });
  };

  // Toggle all tranches in a position
  const handleTogglePositionSelection = (posIndex: number, checked: boolean) => {
    setPositionData(prev => {
      const updated = [...prev];
      
      updated[posIndex].allTranches.forEach(tranche => {
        if (!tranche.isAlreadyImported) {
          tranche.selected = checked;
        }
      });
      updated[posIndex].selected = checked;
      
      return updated;
    });
  };

  const calculateResidualRisk = (
    existingPosition: Position,
    allTranches: TrancheWithSelection[],
    currentTrancheIndex: number
  ): number => {
    // Get existing position's risk amount
    const existingRiskAmount = (existingPosition.avgBuyPrice - existingPosition.stoploss) * existingPosition.totalQuantity;
    
    // Calculate cumulative investment up to current tranche
    let totalInvestment = existingPosition.investedAmount;
    let totalQuantity = existingPosition.totalQuantity;
    
    // Add all selected buy tranches up to and including current tranche
    for (let i = 0; i <= currentTrancheIndex; i++) {
      const tranche = allTranches[i];
      if (tranche.type !== 'buy' || !tranche.selected || tranche.isAlreadyImported) continue;
      
      totalInvestment += tranche.avgPrice * tranche.quantity;
      totalQuantity += tranche.quantity;
    }
    
    // Calculate new stoploss maintaining same risk amount
    const newStoploss = (totalInvestment - existingRiskAmount) / totalQuantity;
    
    return Math.round(newStoploss * 100) / 100;
  };

  const handleStoplossChange = (posIndex: number, trancheIndex: number, value: string) => {
    setPositionData(prev => {
      const updated = [...prev];
      updated[posIndex].allTranches[trancheIndex].stoplossValue = value;
      updated[posIndex].allTranches[trancheIndex].maintainResidualRisk = false;
      updated[posIndex].allTranches[trancheIndex].calculatedStoploss = null;
      return updated;
    });
    
    setValidationErrors(prev => {
      const updated = { ...prev };
      delete updated[`${posIndex}-stoploss`];
      return updated;
    });
  };

  const handleResidualRiskToggle = (posIndex: number, trancheIndex: number, checked: boolean) => {
    setPositionData(prev => {
      const updated = [...prev];
      const position = updated[posIndex];
      const tranche = position.allTranches[trancheIndex];
      const existingPos = existingPositions.find(p => p.isin === position.isin);
      
      tranche.maintainResidualRisk = checked;
      
      if (checked && existingPos) {
        const calculatedSL = calculateResidualRisk(
          existingPos,
          position.allTranches,
          trancheIndex
        );
        tranche.calculatedStoploss = calculatedSL;
        tranche.stoplossValue = '';
      } else {
        tranche.calculatedStoploss = null;
      }
      
      return updated;
    });
    
    setValidationErrors(prev => {
      const updated = { ...prev };
      delete updated[`${posIndex}-stoploss`];
      return updated;
    });
  };

  // Check if there's a previous buy tranche with stoploss
  const hasPreviousBuyWithStoploss = (position: PositionTrancheData, currentIndex: number): boolean => {
    // First check if existing position has stoploss
    if (position.currentTrancheCount > 0 && position.stoploss && position.stoploss > 0) {
      return true;
    }
    
    // Then check previous new buy tranches
    for (let i = 0; i < currentIndex; i++) {
      const t = position.allTranches[i];
      if (t.type === 'buy' && t.selected && !t.isAlreadyImported) {
        if ((t.stoplossValue && parseFloat(t.stoplossValue) > 0) || t.maintainResidualRisk) {
          return true;
        }
      }
    }
    return false;
  };

  // Check if this is the first selected buy tranche
  const isFirstSelectedBuy = (position: PositionTrancheData, currentIndex: number): boolean => {
    for (let i = 0; i < currentIndex; i++) {
      const t = position.allTranches[i];
      if (t.type === 'buy' && t.selected && !t.isAlreadyImported) {
        return false;
      }
    }
    return true;
  };

  // Find the latest selected buy tranche index
  const getLatestSelectedBuyIndex = (position: PositionTrancheData): number => {
    let lastIndex = -1;
    for (let i = 0; i < position.allTranches.length; i++) {
      const t = position.allTranches[i];
      if (t.type === 'buy' && t.selected && !t.isAlreadyImported) {
        lastIndex = i;
      }
    }
    return lastIndex;
  };

  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    
    positionData.forEach((position, posIndex) => {
      if (!position.selected) return;
      
      // Check latest selected buy tranche has stoploss
      const latestBuyIndex = getLatestSelectedBuyIndex(position);
      if (latestBuyIndex >= 0) {
        const latestBuy = position.allTranches[latestBuyIndex];
        const hasStoploss = (latestBuy.stoplossValue && parseFloat(latestBuy.stoplossValue) > 0) || 
                           latestBuy.maintainResidualRisk;
        if (!hasStoploss) {
          errors[`${posIndex}-stoploss`] = `${position.ticker}: Stoploss is required for latest buy tranche`;
        }
      }
    });
    
    setValidationErrors(errors);
    return errors;
  };

  const handleContinue = () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setShowValidationSummary(true);
      // Scroll to top to show validation summary
      const scrollContainer = document.querySelector('.max-h-\\[500px\\]');
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
      return;
    }
    
    setShowValidationSummary(false);
    
    // Convert to format expected by NewPositionsScreen
    const dataForNext = positionData
      .filter(p => p.selected)
      .map(p => {
        // Get selected tranches for import
        const selectedTranches = p.allTranches
          .filter(t => t.selected && !t.isAlreadyImported)
          .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        
        // Build stoploss data from selected buy tranches
        const stoplossData = selectedTranches
          .filter(t => t.type === 'buy')
          .map((t, index) => ({
            trancheIndex: index,
            value: t.stoplossValue,
            maintainResidualRisk: t.maintainResidualRisk,
            calculatedValue: t.calculatedStoploss,
          }));
        
        return {
          ...p,
          newTranches: selectedTranches,
          stoplossData: stoplossData,
        };
      });
    
    onContinue(dataForNext);
  };

  // Count selected tranches
  const getSelectedCounts = (position: PositionTrancheData) => {
    const newTranches = position.allTranches.filter(t => !t.isAlreadyImported);
    const selectedTranches = position.allTranches.filter(t => t.selected && !t.isAlreadyImported);
    const duplicateTranches = position.allTranches.filter(t => t.isAlreadyImported);
    return { newTranches, selectedTranches, duplicateTranches };
  };

  // Total counts
  const totalSelectedTranches = positionData.reduce((sum, p) => 
    sum + p.allTranches.filter(t => t.selected && !t.isAlreadyImported).length, 0
  );
  const totalDuplicates = positionData.reduce((sum, p) => 
    sum + p.allTranches.filter(t => t.isAlreadyImported).length, 0
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
        <span className="ml-2 text-gray-600">Loading positions and checking for duplicates...</span>
      </div>
    );
  }

  if (positionData.length === 0) {
    return (
      <div className="py-6">
        <p className="text-center text-gray-600 mb-6">
          No existing positions found in this import.
        </p>
        <div className="flex justify-end">
          <Button onClick={() => onContinue([])} className="bg-emerald-600 hover:bg-emerald-700">
            Continue to New Stocks →
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Adding tranches to existing positions
      </h3>
      <p className="text-sm text-gray-600 mb-2">
        Select tranches to import and set stoploss for buy tranches. Tranches are shown in chronological order.
      </p>
      
      {/* Validation Error Summary Banner */}
      {showValidationSummary && Object.keys(validationErrors).length > 0 && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-800 mb-2">
                Please fix the following errors before continuing:
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-red-700">
                {Object.values(validationErrors).map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      
      {/* Summary banner */}
      <div className="mb-6 p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <span className="text-gray-700">
            <span className="font-medium text-emerald-600">{totalSelectedTranches}</span> tranche(s) selected for import
          </span>
        </div>
        {totalDuplicates > 0 && (
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span className="text-gray-700">
              <span className="font-medium text-amber-600">{totalDuplicates}</span> already imported
            </span>
          </div>
        )}
      </div>

      <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2">
        {positionData.map((position, posIndex) => {
          const { newTranches, selectedTranches, duplicateTranches } = getSelectedCounts(position);
          const hasNewTranches = newTranches.length > 0;
          const latestBuyIndex = getLatestSelectedBuyIndex(position);
          
          return (
            <Card key={position.isin} className={`p-6 ${!position.selected ? 'bg-gray-50' : ''}`}>
              {/* Position Header */}
              <div className="mb-4 flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id={`select-pos-${posIndex}`}
                      checked={position.selected}
                      disabled={!hasNewTranches}
                      onCheckedChange={(checked) => handleTogglePositionSelection(posIndex, checked as boolean)}
                      className="cursor-pointer"
                    />
                    <label htmlFor={`select-pos-${posIndex}`} className="cursor-pointer">
                      <h4 className={`text-lg font-semibold ${position.selected ? 'text-gray-900' : 'text-gray-500'}`}>
                        {position.ticker}
                      </h4>
                    </label>
                    <span className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-600">
                      {position.exchange}
                    </span>
                  </div>
                  <p className={`text-sm ml-8 ${position.selected ? 'text-gray-600' : 'text-gray-400'}`}>
                    Existing Position - Currently {position.currentTrancheCount} tranche(s)
                  </p>
                  <div className="ml-8 flex items-center gap-3 mt-1">
                    {selectedTranches.length > 0 && (
                      <span className="text-sm text-emerald-600 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        {selectedTranches.length} selected for import
                      </span>
                    )}
                    {duplicateTranches.length > 0 && (
                      <span className="text-sm text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {duplicateTranches.length} already imported
                      </span>
                    )}
                  </div>
                  <p className={`text-xs ml-8 ${position.selected ? 'text-gray-500' : 'text-gray-400'}`}>
                    ISIN: {position.isin}
                  </p>
                </div>
              </div>

              {/* Tranches - Chronological Order */}
              <div className={`space-y-3 ${!position.selected && hasNewTranches ? 'opacity-40' : ''}`}>
                <p className="font-medium text-gray-700">Tranches:</p>
                
                {position.allTranches.map((tranche, trancheIndex) => {
                  const isAlreadyImported = tranche.isAlreadyImported;
                  const isBuy = tranche.type === 'buy';
                  const isLatestBuy = trancheIndex === latestBuyIndex;
                  const isFirstBuy = isBuy && !isAlreadyImported && tranche.selected && isFirstSelectedBuy(position, trancheIndex);
                  const showResidualRisk = isBuy && !isAlreadyImported && tranche.selected && 
                                          (position.currentTrancheCount > 0 || !isFirstBuy) && 
                                          hasPreviousBuyWithStoploss(position, trancheIndex);
                  
                  return (
                    <div
                      key={tranche.orderId}
                      className={`border-l-4 pl-4 py-3 ${
                        isAlreadyImported 
                          ? 'border-amber-300 bg-amber-50 opacity-60' 
                          : tranche.selected
                            ? isLatestBuy
                              ? 'border-emerald-500 bg-emerald-50'
                              : isBuy
                                ? 'border-blue-300 bg-blue-50'
                                : 'border-red-300 bg-red-50'
                            : 'border-gray-200 bg-gray-50 opacity-60'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-start gap-3">
                          {/* Tranche Selection Checkbox */}
                          <Checkbox
                            id={`tranche-${posIndex}-${trancheIndex}`}
                            checked={tranche.selected}
                            disabled={isAlreadyImported || !position.selected}
                            onCheckedChange={(checked) => handleToggleTrancheSelection(posIndex, trancheIndex, checked as boolean)}
                            className="mt-1 cursor-pointer"
                          />
                          <div>
                            <p className="font-medium text-gray-900">
                              {new Date(tranche.timestamp).toLocaleDateString()} - {' '}
                              <span className={isBuy ? 'text-blue-600' : 'text-red-600'}>
                                {isBuy ? 'BUY' : 'SELL'}
                              </span>
                              {isLatestBuy && (
                                <span className="ml-2 text-emerald-600 text-sm">★ LATEST BUY</span>
                              )}
                            </p>
                            <p className="text-sm text-gray-600">
                              {tranche.quantity} shares @ ₹{tranche.avgPrice}
                            </p>
                            <p className="text-xs text-gray-500">
                              Order ID: {tranche.orderId}
                            </p>
                          </div>
                        </div>
                        {isAlreadyImported && (
                          <span className="text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded whitespace-nowrap">
                            Already Imported
                          </span>
                        )}
                      </div>

                      {/* Stoploss Input - Only for selected new buy tranches */}
                      {isBuy && !isAlreadyImported && tranche.selected && (
                        <div className="ml-8 space-y-3">
                          {/* Residual Risk Option */}
                          {showResidualRisk && (
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`residual-${posIndex}-${trancheIndex}`}
                                checked={tranche.maintainResidualRisk}
                                onCheckedChange={(checked) =>
                                  handleResidualRiskToggle(posIndex, trancheIndex, checked as boolean)
                                }
                              />
                              <label
                                htmlFor={`residual-${posIndex}-${trancheIndex}`}
                                className="text-sm font-medium leading-none"
                              >
                                Maintain Residual Risk
                                {tranche.maintainResidualRisk && tranche.calculatedStoploss !== null && (
                                  <span className="ml-2 text-emerald-600">
                                    (₹{tranche.calculatedStoploss})
                                  </span>
                                )}
                              </label>
                            </div>
                          )}
                          
                          {/* Manual Stoploss Input */}
                          {!tranche.maintainResidualRisk && (
                            <div>
                              <Label
                                htmlFor={`sl-${posIndex}-${trancheIndex}`}
                                className={isLatestBuy ? 'text-gray-900' : 'text-gray-600'}
                              >
                                {showResidualRisk ? 'Or enter stoploss manually' : 'Stoploss'}
                                {isLatestBuy && <span className="text-red-500"> *</span>}
                                {tranche.stoplossValue && parseFloat(tranche.stoplossValue) > 0 && (
                                  <span className="ml-2 text-sm font-normal text-emerald-600">
                                    ({((tranche.avgPrice - parseFloat(tranche.stoplossValue)) / tranche.avgPrice * 100).toFixed(1)}% below entry)
                                  </span>
                                )}
                              </Label>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="relative flex-1">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                                  <Input
                                    id={`sl-${posIndex}-${trancheIndex}`}
                                    type="number"
                                    step="0.01"
                                    placeholder={isLatestBuy ? 'Required' : 'Optional'}
                                    value={tranche.stoplossValue}
                                    onChange={(e) =>
                                      handleStoplossChange(posIndex, trancheIndex, e.target.value)
                                    }
                                    className={`pl-7 ${
                                      isLatestBuy && validationErrors[`${posIndex}-stoploss`]
                                        ? 'border-red-500'
                                        : ''
                                    }`}
                                  />
                                </div>
                              </div>
                              {isLatestBuy && validationErrors[`${posIndex}-stoploss`] && (
                                <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  {validationErrors[`${posIndex}-stoploss`]}
                                </p>
                              )}
                              {!isLatestBuy && !isFirstBuy && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Optional - Can be set later
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Sell tranche note */}
                      {!isBuy && !isAlreadyImported && tranche.selected && (
                        <p className="ml-8 text-xs text-gray-500">
                          No stoploss needed for sell orders
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>

      <div className="flex justify-between items-center mt-6 pt-4 border-t">
        <p className="text-sm text-gray-600">
          {positionData.filter(p => p.selected).length} of {positionData.length} positions selected
          {totalDuplicates > 0 && (
            <span className="text-amber-600 ml-2">
              ({totalDuplicates} duplicate tranche(s) will be skipped)
            </span>
          )}
        </p>
        <Button
          onClick={handleContinue}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          Continue to New Stocks →
        </Button>
      </div>
    </div>
  );
};

export default ExistingPositionsScreen;
